import { createRoute } from '@tanstack/react-router';
import { Suspense, lazy, type ComponentType } from 'react';
import { z } from 'zod';
import { LoadingState } from '@buildbee/ui';
import { featureGate } from '../../router/gates';
import { appLayoutRoute } from '../../router/parents';
import { FinancialsLayout } from './pages/layout';

/**
 * Financials routes (module-owned). `/financials` is the module layout with section tabs;
 * every route of the module is exported in `financialsRoutes`. Pages are code-split.
 */

function page<P extends object>(load: () => Promise<ComponentType<P>>) {
  const Lazy = lazy(async () => ({ default: await load() })) as unknown as ComponentType<P>;
  return function LazyPage(props: P) {
    return (
      <Suspense fallback={<LoadingState />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
}

const OverviewPage = page(() => import('./pages/overview').then((m) => m.OverviewPage));
const InvoicesPage = page(() => import('./pages/invoices').then((m) => m.InvoicesPage));
const InvoiceDetailPage = page(() => import('./pages/invoices').then((m) => m.InvoiceDetailPage));
const InvoicePrintPage = page(() => import('./pages/invoices').then((m) => m.InvoicePrintPage));
const ChangeOrdersPanel = page(() => import('./pages/changes').then((m) => m.ChangeOrdersPanel));
const CommitmentsPanel = page(() => import('./pages/commitments').then((m) => m.CommitmentsPanel));
const SubPayAppsPanel = page(() => import('./pages/sub-pay-apps').then((m) => m.SubPayAppsPanel));
const LienWaiversPanel = page(() => import('./pages/waivers').then((m) => m.LienWaiversPanel));
const ReportsPage = page(() => import('./pages/reports').then((m) => m.ReportsPage));
const AccountingPage = page(() => import('./pages/accounting').then((m) => m.AccountingPage));
const CostCodesPage = page(() => import('./pages/cost-codes').then((m) => m.CostCodesPage));
const ProjectFinancialsPage = page(() => import('./pages/project').then((m) => m.ProjectFinancialsPage));

const PROJECT_TABS = ['budget', 'commitments', 'change-events', 'change-orders', 'billing', 'sub-pay-apps', 'lien-waivers'] as const;

const financialsLayoutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/financials',
  component: FinancialsLayout,
});

const overviewRoute = createRoute({ getParentRoute: () => financialsLayoutRoute, path: '/', component: () => <OverviewPage /> });

const invoicesRoute = createRoute({ getParentRoute: () => financialsLayoutRoute, path: 'invoices', component: () => <InvoicesPage /> });

const invoiceRoute = createRoute({
  getParentRoute: () => financialsLayoutRoute,
  path: 'invoices/$invoiceId',
  component: function InvoiceRoute() {
    const { invoiceId } = invoiceRoute.useParams();
    return <InvoiceDetailPage invoiceId={invoiceId} />;
  },
});

const invoicePrintRoute = createRoute({
  getParentRoute: () => financialsLayoutRoute,
  path: 'invoices/$invoiceId/print',
  component: function InvoicePrintRoute() {
    const { invoiceId } = invoicePrintRoute.useParams();
    return <InvoicePrintPage invoiceId={invoiceId} />;
  },
});

const changeOrdersRoute = createRoute({ getParentRoute: () => financialsLayoutRoute, path: 'change-orders', component: () => <ChangeOrdersPanel /> });
const commitmentsRoute = createRoute({ getParentRoute: () => financialsLayoutRoute, path: 'commitments', component: () => <CommitmentsPanel /> });
// Hidden for the first release (lib/features.ts): these redirect to the Financials overview.
const payAppsRoute = createRoute({ getParentRoute: () => financialsLayoutRoute, path: 'pay-apps', beforeLoad: featureGate('fin.pay-apps', '/financials'), component: () => <SubPayAppsPanel /> });
const waiversRoute = createRoute({ getParentRoute: () => financialsLayoutRoute, path: 'lien-waivers', beforeLoad: featureGate('fin.lien-waivers', '/financials'), component: () => <LienWaiversPanel /> });
const reportsRoute = createRoute({ getParentRoute: () => financialsLayoutRoute, path: 'reports', component: () => <ReportsPage /> });
const costCodesRoute = createRoute({ getParentRoute: () => financialsLayoutRoute, path: 'cost-codes', component: () => <CostCodesPage /> });

const accountingRoute = createRoute({
  getParentRoute: () => financialsLayoutRoute,
  path: 'accounting',
  beforeLoad: featureGate('fin.accounting', '/financials'),
  validateSearch: z.object({ connected: z.string().optional(), error: z.string().max(500).optional() }),
  component: function AccountingRoute() {
    const search = accountingRoute.useSearch();
    return <AccountingPage connected={search.connected} error={search.error} />;
  },
});

const projectRoute = createRoute({
  getParentRoute: () => financialsLayoutRoute,
  path: 'projects/$projectId',
  validateSearch: z.object({ tab: z.enum(PROJECT_TABS).catch('budget').default('budget') }),
  component: function ProjectRoute() {
    const { projectId } = projectRoute.useParams();
    const { tab } = projectRoute.useSearch();
    return <ProjectFinancialsPage projectId={projectId} tab={tab} />;
  },
});

export const financialsRoutes = [
  financialsLayoutRoute.addChildren([
    overviewRoute,
    invoicesRoute,
    invoiceRoute,
    invoicePrintRoute,
    changeOrdersRoute,
    commitmentsRoute,
    payAppsRoute,
    waiversRoute,
    reportsRoute,
    accountingRoute,
    costCodesRoute,
    projectRoute,
  ]),
] as const;
